import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'emr_auth_token';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('emr_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  register: (data) => api.post('/auth/register', data),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Patients API
export const patientsAPI = {
  getAll: (params) => api.get('/patients', { params }),
  getById: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  delete: (id) => api.delete(`/patients/${id}`),
  getTimeline: (id) => api.get(`/patients/${id}/timeline`),
  getAttachments: (id) => api.get(`/patients/${id}/attachments`),
  getSummary: (id) => api.get(`/patients/${id}/summary`),
};

// Forms API
export const formsAPI = {
  // Templates
  getTemplates: (params) => api.get('/forms/templates', { params }),
  getTemplateById: (id) => api.get(`/forms/templates/${id}`),
  createTemplate: (data) => api.post('/forms/templates', data),
  updateTemplate: (id, data) => api.put(`/forms/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/forms/templates/${id}`),
  
  // Submissions
  getSubmissions: (params) => api.get('/forms/submissions', { params }),
  getSubmissionById: (id) => api.get(`/forms/submissions/${id}`),
  createSubmission: (data) => api.post('/forms/submissions', data),
};

// Clinical Notes API
export const clinicalNotesAPI = {
  transcribe: (audioFile) => {
    const formData = new FormData();
    formData.append('audio', audioFile);
    return api.post('/clinical-notes/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  generate: (data) => api.post('/clinical-notes/generate', data),
  approve: (noteId, data) => api.post(`/clinical-notes/${noteId}/approve`, data),
  getAll: (params) => api.get('/clinical-notes', { params }),
  getById: (id) => api.get(`/clinical-notes/${id}`),
};

// Invoices API
export const invoicesAPI = {
  create: (data) => api.post('/invoices', data),
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  updateStatus: (id, status) => api.put(`/invoices/${id}/status?status=${status}`),
};

// Event Quotes API
export const eventQuotesAPI = {
  submit: (data) => api.post('/event-quotes', data),
  getAll: (params) => api.get('/event-quotes', { params }),
  getById: (id) => api.get(`/event-quotes/${id}`),
  updateStatus: (id, status) => api.put(`/event-quotes/${id}/status?status=${status}`),
  getFinancialSummary: (id) => api.get(`/event-quotes/${id}/financial-summary`),
  getStats: () => api.get('/event-quotes-stats'),
  // Admin create event directly
  adminCreate: (data) => api.post('/event-quotes/admin-create', data),
  // Delete (soft/hard)
  delete: (id, permanent = false, cascade = true) => 
    api.delete(`/event-quotes/${id}`, { params: { permanent, cascade } }),
  restore: (id) => api.put(`/event-quotes/${id}/restore`),
};

// Event Invoices API
export const eventInvoicesAPI = {
  create: (data) => api.post('/event-invoices', data),
  getAll: (params) => api.get('/event-invoices', { params }),
  getById: (id) => api.get(`/event-invoices/${id}`),
  update: (id, data) => api.put(`/event-invoices/${id}`, data),
  getByQuote: (quoteId) => api.get('/event-invoices', { params: { quote_request_id: quoteId } }),
  // Delete (soft/hard)
  delete: (id, permanent = false) => api.delete(`/event-invoices/${id}`, { params: { permanent } }),
  restore: (id) => api.put(`/event-invoices/${id}/restore`),
};

// Event Invoice Payments API
export const eventInvoicePaymentsAPI = {
  create: (data) => api.post('/event-invoice-payments', data),
  getByInvoice: (invoiceId) => api.get('/event-invoice-payments', { params: { invoice_id: invoiceId } }),
  delete: (id) => api.delete(`/event-invoice-payments/${id}`),
};

// Files API
export const filesAPI = {
  upload: (patientId, file, fileType = 'document') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/files/upload?patient_id=${patientId}&file_type=${fileType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (fileId) => api.get(`/files/${fileId}`, { responseType: 'blob' }),
};

// Audit Logs API
export const auditAPI = {
  getLogs: (params) => api.get('/audit-logs', { params }),
};

// Email Logs API
export const emailLogsAPI = {
  getAll: (params) => api.get('/email-logs', { params }),
  retry: (logId) => api.post(`/email-logs/${logId}/retry`),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Export API
export const exportAPI = {
  patients: (format = 'csv') => api.get(`/export/patients?format=${format}`, { responseType: 'blob' }),
  submissions: (params) => api.get('/export/submissions', { params, responseType: 'blob' }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentDocuments: (limit = 10) => api.get('/dashboard/recent-documents', { params: { limit } }),
};

// Config API
export const configAPI = {
  getClinic: () => api.get('/config/clinic'),
};

// AI API
export const aiAPI = {
  getICD10Codes: (diagnosisText, maxCodes = 5) => 
    api.post('/ai/icd10-codes', { diagnosis_text: diagnosisText, max_codes: maxCodes }),
  getICD10CodesSimple: (diagnosis) => 
    api.get('/ai/icd10-codes', { params: { diagnosis } }),
  generateReport: (data) => api.post('/ai/generate-report', data),
  finalizeReport: (reportId, finalText) => 
    api.put(`/ai/reports/${reportId}/finalize`, null, { params: { final_text: finalText } }),
  getReports: (patientId) => api.get('/ai/reports', { params: { patient_id: patientId } }),
  getSpecialistTypes: () => api.get('/ai/specialist-types'),
};

// Google Sheets Integration API
export const integrationsAPI = {
  configureGoogleSheets: (data) => api.post('/integrations/google-sheets/configure', data),
  getGoogleSheetsConfigs: () => api.get('/integrations/google-sheets/configs'),
  syncGoogleSheets: (configId) => api.post(`/integrations/google-sheets/sync/${configId}`),
};

// Patient Requests API
export const patientRequestsAPI = {
  submit: (data) => api.post('/patient-requests', data),
  getAll: (params) => api.get('/patient-requests', { params }),
  getById: (id) => api.get(`/patient-requests/${id}`),
  updateStatus: (id, status, notes) => api.put(`/patient-requests/${id}/status?status=${status}`, null, { params: { notes } }),
  // Duplicate checking and conversion
  checkDuplicates: (id) => api.post(`/patient-requests/${id}/check-duplicates`),
  convertToPatient: (id, data = {}) => api.post(`/patient-requests/${id}/convert-to-patient`, data),
};

// Appointments API
export const appointmentsAPI = {
  // CRUD operations
  create: (data) => api.post('/appointments', data),
  getAll: (params) => api.get('/appointments', { params }),
  getById: (id) => api.get(`/appointments/${id}`),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  updateStatus: (id, status) => api.put(`/appointments/${id}/status?status=${status}`),
  delete: (id) => api.delete(`/appointments/${id}`),
  
  // Calendar and agenda views
  getCalendarEvents: (startDate, endDate, providerId = null) => {
    const params = { start_date: startDate, end_date: endDate };
    if (providerId) params.provider_id = providerId;
    return api.get('/appointments/calendar', { params });
  },
  getAgenda: (date, providerId = null) => {
    const params = { date };
    if (providerId) params.provider_id = providerId;
    return api.get('/appointments/agenda', { params });
  },
  
  // Stats
  getStats: (dateFrom, dateTo) => api.get('/appointments/stats', { params: { date_from: dateFrom, date_to: dateTo } }),
  
  // Reminders
  getPendingReminders: (limit = 100) => api.get('/appointments/reminders/pending', { params: { limit } }),
  getReminderLog: (params) => api.get('/appointments/reminders/log', { params }),
  sendReminderNow: (appointmentId, type = 'both') => api.post(`/appointments/${appointmentId}/reminders/send?reminder_type=${type}`),
  
  // Communication preferences
  getPatientPreferences: (patientId) => api.get(`/appointments/patients/${patientId}/communication-preferences`),
  updatePatientPreferences: (patientId, data) => api.put(`/appointments/patients/${patientId}/communication-preferences`, data),
  optOut: (patientId, channel) => api.post(`/appointments/patients/${patientId}/opt-out/${channel}`),
  optIn: (patientId, channel) => api.post(`/appointments/patients/${patientId}/opt-in/${channel}`),
  
  // Communication logs
  getCommunicationLogs: (params) => api.get('/appointments/communication-logs', { params }),
  
  // Metadata
  getTypes: () => api.get('/appointments/types'),
  getReminderTimings: () => api.get('/appointments/reminder-timings'),
};

// Prescriptions API
export const prescriptionsAPI = {
  create: (data) => api.post('/prescriptions', data),
  getAll: (params) => api.get('/prescriptions', { params }),
  getById: (id) => api.get(`/prescriptions/${id}`),
  getByPatient: (patientId) => api.get('/prescriptions', { params: { patient_id: patientId } }),
  void: (id) => api.put(`/prescriptions/${id}/void`),
};

// Lab Requests API
export const labRequestsAPI = {
  getCategories: () => api.get('/lab-tests/categories'),
  create: (data) => api.post('/lab-requests', data),
  getAll: (params) => api.get('/lab-requests', { params }),
  getById: (id) => api.get(`/lab-requests/${id}`),
  getByPatient: (patientId) => api.get('/lab-requests', { params: { patient_id: patientId } }),
  update: (id, data) => api.put(`/lab-requests/${id}`, data),
  updateStatus: (id, status) => api.put(`/lab-requests/${id}/status?status=${status}`),
  delete: (id) => api.delete(`/lab-requests/${id}`),
  sendEmail: (id, email) => api.post(`/lab-requests/${id}/email`, null, { params: { recipient_email: email } }),
};

// Patient History API
export const patientHistoryAPI = {
  get: (patientId) => api.get(`/patients/${patientId}/history`),
  update: (patientId, data) => api.put(`/patients/${patientId}/history`, data),
  addCondition: (patientId, condition) => api.post(`/patients/${patientId}/history/condition`, condition),
  addAllergy: (patientId, allergy) => api.post(`/patients/${patientId}/history/allergy`, allergy),
  addMedication: (patientId, medication) => api.post(`/patients/${patientId}/history/medication`, medication),
  getCommonConditions: () => api.get('/common-conditions'),
};

// Labs API
export const labsAPI = {
  uploadDocument: (formData) => api.post('/labs/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getDocuments: (patientId) => api.get(`/labs/documents/${patientId}`),
  extractResults: (labDocId) => api.post(`/labs/extract/${labDocId}`),
  saveResults: (data) => api.post('/labs/results', data),
  getResults: (patientId, params) => api.get(`/labs/results/${patientId}`, { params }),
  updateResult: (resultId, data) => api.put(`/labs/results/${resultId}`, data),
  deleteResult: (resultId) => api.delete(`/labs/results/${resultId}`),
  manualEntry: (data) => api.post('/labs/manual-entry', data),
  getTrends: (patientId, testName) => api.get(`/labs/trends/${patientId}/${encodeURIComponent(testName)}`),
  getCategories: (patientId) => api.get(`/labs/categories/${patientId}`),
  generateSummary: (patientId) => api.post(`/labs/ai-summary/${patientId}`),
  getLatestSummary: (patientId) => api.get(`/labs/ai-summary/${patientId}`),
};

// Encounters API
export const encountersAPI = {
  create: (data) => api.post('/encounters', data),
  getForPatient: (patientId, params) => api.get(`/encounters/patient/${patientId}`, { params }),
  get: (encounterId) => api.get(`/encounters/${encounterId}`),
  update: (encounterId, data) => api.put(`/encounters/${encounterId}`, data),
  delete: (encounterId) => api.delete(`/encounters/${encounterId}`),
  generateDocument: (data) => api.post('/encounters/generate-document', data),
};

export default api;
